import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../modules/layout/components/Loader';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import { useToast } from '../../modules/layout/components/ToastContext';
import { getAll, updateDetails } from '../_request';
import { IProfileDetailsDto } from '../../interfaces/IUtilities';
import DeleteProfile from './DeleteProfile';
import DisableProfile from './DisableProfile';
import ActivateProfile from './ActivateProfile';
import ApproveProfile from './ApproveProfile';
import ResetPassword from './ResetPassword';
import VerifyUserEmail from './VerifyEmail';
import { GetActionLink } from './GetActionLink';
import UnlockUser from './UnlockUser';
import ResendActivationLink from './ResendActivationLink';
import SendResetPassword from './SendResetPasswordSms';
import AssignOtpAppSetup from './AssignOtpApp';
import UnassignOtpAppSetup from './UnassignOtpApp';
import { Tooltip } from 'bootstrap';
import ChangeFullName from './ChangeFullName';
import SpokeAllowedTransactions from './SpokeAllowedTransactions';
import { useAuth, userHasRole } from '../../../setup/auth/authService'
import { AdminWeb, ManageProfile, HelpDeskOperator, ProfileType } from '../../constants/constants';

export default function ProfileDetails(){
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
    
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<IProfileDetailsDto | null>(null);

  const { targetProfileId } = useParams<{ targetProfileId: string }>();
  const uniqueId = profile?.PPID;

  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({
    Email: '',
    MobileNumber: '',
    ProfileType:'',
  });

  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [clearMobileNumber, setClearMobileNumber] = useState(false);

  const mobileCleared = clearMobileNumber || (editedProfile.MobileNumber || '').trim() === '';
  const mobileWillBeCleared = mobileCleared && (profile?.MobileNumber || '').trim() !== '';

  const { user } = useAuth();
  const [isAdminWeb, setIsAdminWeb] = useState(false);
  const [isManageProfile, setIsManageProfile] = useState(false);
  const [isHelpDeskOperator, setIsHelpDeskOperator] = useState(false);

  const editInputStyle = {
    width: '0',
    flex: '1 1 0',
    maxWidth: '220px',
    textAlign: 'right' as const,
  };
    
  useEffect(() => {
    const checkUserRole = async () => {
      const hasAdminWebRole = await userHasRole(AdminWeb);
      const hasManageProfileRole = await userHasRole(ManageProfile);
      const hasHelpDeskOperatorRole = await userHasRole(HelpDeskOperator);
      setIsAdminWeb(hasAdminWebRole);
      setIsManageProfile(hasManageProfileRole);
      setIsHelpDeskOperator(hasHelpDeskOperatorRole);
    };  
    if (user && !user.expired) {
      checkUserRole();
    }
  }, [user]);

   useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new Tooltip(tooltipTriggerEl);
    });
  }, []);

  useEffect(() => {
    if (profile) {
      setEditedProfile({
        Email: profile.Email || '',
        MobileNumber: profile.MobileNumber || '',
        ProfileType: profile.ProfileType || '',
      });
    }
  }, [profile]);

  useEffect(() => {
  if (profile) {
    const rawType = profile.ProfileType || profile.Category || '';
    const normalizedType =
      rawType.toLowerCase() === 'individual'
        ? 'Individual'
        : rawType.toLowerCase() === 'organisation'
        ? 'Organisation'
        : rawType;

    setEditedProfile({
      Email: profile.Email || '',
      MobileNumber: profile.MobileNumber || '',
      ProfileType: normalizedType,
    });
    }
  }, [profile]);

  const handleSave = async () => {
  if (!targetProfileId || !profile) return;
 
  const currentEmail = profile.Email || '';
  const currentMobile = profile.MobileNumber || '';
  const currentType = profile.ProfileType || profile.Category || '';
 
  const targetEmail = editedProfile.Email;
  const targetMobile = mobileCleared ? '' : editedProfile.MobileNumber;
  const targetType = editedProfile.ProfileType;
 
  const hasChanges =
    currentEmail !== targetEmail ||
    currentMobile !== targetMobile ||
    currentType !== targetType;
 
  // If no modifications are detected, exit immediately and close edit state
  if (!hasChanges) {
    setIsEditing(false);
    return;
  }

  const errors: Record<string, string> = {};

  if (editedProfile.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedProfile.Email)) {
    errors.Email = t('InvalidEmailFormat');
  }

  if (Object.keys(errors).length > 0) {
    setEditErrors(errors);
    return;
  }
  setEditErrors({})
  setLoading(true);

  const payload = {
    name: profile?.Name,
    email: editedProfile.Email,
    description: profile?.Description,
    languageCode: profile?.LanguageCode,
    mobileNumber: targetMobile,
    clearMobileNumber: mobileCleared,
    profileType: editedProfile.ProfileType,
  };

   try {
    const result: any = await updateDetails(
      `/api/UserEvents/setprofiledetails?targetProfileId=${encodeURIComponent(targetProfileId)}`,
      payload,
      true  // throwOnError
    );

       setProfile((prev) =>
           prev
               ? {
                   ...prev,
                   Email: editedProfile.Email,
                   MobileNumber: targetMobile,
                   ProfileType: editedProfile.ProfileType,
                   Category: editedProfile.ProfileType,
               }
               : prev
       );

       showToast && showToast(t('ProfileUpdatedSuccessfully'), 'success');

    setClearMobileNumber(false);
    setIsEditing(false);

  } catch (err: any) {
      const status = err?.response?.status;
      const messageText = err?.response?.data?.Message || err?.response?.data?.message || "";

      if (status === 409) {
        const lowerMsg = messageText.toLowerCase();
        const serverErrors: Record<string, string> = {};
        if (lowerMsg.includes('mobile number')) serverErrors.MobileNumber = t('MobileNumberAlreadyInUse');
        if (lowerMsg.includes('email')) serverErrors.Email = t('EmailAlreadyInUse');
        if (Object.keys(serverErrors).length > 0) { setEditErrors(serverErrors); return; }
        showToast && showToast(messageText || t('ConflictError'), 'error');
        return;
      }

      if (status === 401) { navigate('/auth/login'); return; }
      if (status === 403) { navigate('/error/403'); return; }
      if (status === 500) { navigate('/error/500'); return; }

      showToast && showToast(t('SomethingWentWrong'), 'error');
    } finally {
    setLoading(false);
  }
};

    const fetchProfileDetails = async () => {
      if (!targetProfileId) return;
      setLoading(true);
      setError(null);
      try {
        const result: any = await getAll(`/api/UserEvents/getprofiledetails`,`?targetProfileId=${encodeURIComponent(targetProfileId)}`);
        const respStatus = result?.response?.status || result?.status;
          
        if (respStatus === 200 || respStatus === undefined) {
          const payload = result?.data?.Data ?? result?.data ?? result;
          setProfile(payload as IProfileDetailsDto);
              
          if (!payload && showToast) {
            showToast(t('ProfileNotFound'), 'info');
          }
        } else if (respStatus === 403) {
          navigate('/error/403');
        } else if (respStatus === 401) {
          navigate('/auth/login');
        } else if (respStatus === 404) {
          showToast && showToast(t('ProfileNotFound'), 'error');
          navigate('/profiles');
        } else if (respStatus === 500) {
          navigate('/error/500');
        } else {
          showToast && showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        }
      } catch (err: any) {
        console.error(err);
        setError(String(err?.message ?? err));
        showToast && showToast(t('SomethingWentWrong'), 'error');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (!targetProfileId) {
        showToast && showToast(t('NoProfileIdProvided'), 'warning');
        navigate(`/find-profiles`);
        return;
      }
  
    fetchProfileDetails();
    }, [targetProfileId]);

    const handleProfileUpdate = (updatedFields: Partial<IProfileDetailsDto>) => {
      setProfile((prev) => (prev ? { ...prev, ...updatedFields } : null));
    };

    const InfoItem = ({
      label,
      value,
    }: {
      label: string;
      value?: React.ReactNode;
    }) => (
      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
        <span className="text-muted">{label}</span>
        <span className="fw-semibold text-end">{value || '-'}</span>
      </div>
    );
   

return(
  <div className="container mt-4">
    {(isHelpDeskOperator && isAdminWeb)  &&(
      <>   
        <AppBreadcrumb items={[
          { title: t('Home'), href: '/' },
          { title: t('UserProfilesList'), href: '/find-profiles' },
          { title: t('ProfileDetails') }
        ]} />

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">{t('ProfileDetails')}</h3>         
        </div>

        {loading && <Loader />}
        {error && <div className="alert alert-danger">{error}</div>}

        {profile  && (
          <div className="row g-4">
            {/* Left side*/}
            
            <div className="col-12 col-lg-8">
              <div className="card shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                  <div className="d-flex align-items-center gap-3">
                    <h5 className="mb-0 fw-bold">{profile.Name}</h5>
                    {targetProfileId  && (isManageProfile && isAdminWeb) && (
                      <ChangeFullName 
                        targetProfileId={targetProfileId} 
                        onSuccess={fetchProfileDetails}
                      />
                    )}
                  </div> 
                  {(isManageProfile && isAdminWeb) && (
                    <div className="d-flex gap-2">
                      {isEditing ? (
                        <>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setEditedProfile({
                                Email: profile.Email || '',
                                MobileNumber: profile.MobileNumber || '',
                                ProfileType: profile.ProfileType || profile.Category || '',
                              });
                              setEditErrors({});
                              setClearMobileNumber(false);
                              setIsEditing(false);
                            }}
                          >
                            {t('Cancel')}
                          </button>

                          <button
                            className="btn btn-success btn-sm"
                            onClick={handleSave}
                          >
                            {t('Save')}
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-dark btn-sm"
                          onClick={() => setIsEditing(true)}
                        >
                          {t('Edit')}
                        </button>
                      )}
                    </div>
                  )}                                        
                </div>

                <div className="card-body">
                  <div className="row ">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <h5 className="mb-0 text-muted" style={{ fontSize: '1rem' }}>{t('Status')}:</h5>
                      <span 
                        className={`badge rounded-pill ${profile.ProfileState === 'Active' ? 'bg-success' : 'bg-secondary'}`}
                        style={{ padding: '0.5em 0.8em', fontSize: '0.85rem' }}
                      >
                        {profile.ProfileState}
                      </span>
                    </div>
                    <div className="col-md-6"><InfoItem label={t('Username')} value={profile.Username} /></div>
                    <div className="col-md-6"><InfoItem label={t('UserType')} value={profile.UserType} /></div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <span className="text-muted">{t('MobileNumber')}</span>
                        {isEditing ? (
                          <div className="d-flex flex-column align-items-end ms-3" style={{ flex: '1 1 0', maxWidth: '220px' }}>
                            <div className="input-group input-group-sm">
                              <input
                                className={`form-control form-control-sm ${editErrors.MobileNumber ? 'error' : ''}`}
                                value={clearMobileNumber ? '' : editedProfile.MobileNumber}
                                placeholder={mobileWillBeCleared ? t('WillBeCleared') : undefined}
                                onChange={(e) => {
                                  setClearMobileNumber(false);
                                  setEditedProfile({ ...editedProfile, MobileNumber: e.target.value });
                                  if (editErrors.MobileNumber)
                                    setEditErrors(prev => { const { MobileNumber: _, ...rest } = prev; return rest; });
                                }}
                              />
                              {(editedProfile.MobileNumber || clearMobileNumber) && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  title={t('ClearMobileNumber')}
                                  onClick={() => {
                                    setClearMobileNumber(true);
                                    setEditedProfile({ ...editedProfile, MobileNumber: '' });
                                    if (editErrors.MobileNumber)
                                      setEditErrors(prev => { const { MobileNumber: _, ...rest } = prev; return rest; });
                                  }}
                                >
                                  <i className="bi bi-x-lg" />
                                </button>
                              )}
                            </div>
                            {mobileWillBeCleared && (
                              <span className="text-warning" style={{ fontSize: '0.75rem' }}>
                                {t('MobileNumberWillBeCleared')}
                              </span>
                            )}
                            {editErrors.MobileNumber && (
                              <span className="text-danger" style={{ fontSize: '0.75rem' }}>
                                {editErrors.MobileNumber}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="fw-semibold text-end">{profile.MobileNumber || '-'}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <span className="text-muted">{t('Email')}</span>
                        {isEditing ? (
                          <div className="d-flex flex-column align-items-end ms-3" style={{ flex: '1 1 0', maxWidth: '220px' }}>
                            <input
                              type="email"
                              className={`form-control form-control-sm ${editErrors.Email ? 'error' : ''}`}
                              value={editedProfile.Email}
                              onChange={(e) => {
                                setEditedProfile({ ...editedProfile, Email: e.target.value });
                                if (editErrors.Email)
                                  setEditErrors(prev => { const { Email: _, ...rest } = prev; return rest; });
                              }}
                            />
                            {editErrors.Email && (
                              <span className="text-danger" style={{ fontSize: '0.75rem' }}>
                                {editErrors.Email}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="fw-semibold text-end">{profile.Email || '-'}</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-between align-items-center py-2 border-bottom gap-3">
                        <span className="text-muted">{t('Category')}</span>

                        {isEditing && (editedProfile.ProfileType === 'Individual' || editedProfile.ProfileType === 'Organisation' || editedProfile.ProfileType === 'None' || !editedProfile.ProfileType) ? (
                          <select
                            className="form-select form-select-sm ms-3"
                            style={editInputStyle}
                            value={editedProfile.ProfileType}
                            onChange={(e) =>
                              setEditedProfile({ ...editedProfile, ProfileType: e.target.value })
                            }
                          >
                            {!editedProfile.ProfileType && (
                              <option value="" disabled>Select</option>
                            )}
                            <option value="Individual">Individual</option>
                            <option value="Organisation">Organisation</option>
                          </select>
                        ) : (
                          <span className="fw-semibold text-end">
                            {profile.ProfileType || profile.Category || '-'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6"><InfoItem label={t('CreatedBy')} value={profile.CreatedBy} /></div>
                    <div className="col-md-6"><InfoItem label={t('PPID')} value={profile.PPID} /></div>
                    <div className="col-md-6"><InfoItem label={t('Approved')} value={profile.Approved ? t('Yes') : t('No')} /></div>
                    
                    <div className="col-12 mt-4">
                      <h6 className="text-muted small text-uppercase fw-bold border-bottom pb-2 mt-4">
                        {t('SystemIdentifiers')}
                      </h6>
                    </div>
                    <div className="col-12"><InfoItem label={t('UserId')} value={profile.UserId} /></div>
                    <div className="col-12"><InfoItem label={t('ProfileId')} value={profile.ProfileId} /></div>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-3">
                {profile && (isManageProfile && isAdminWeb) && (
                  <>               
                    {profile.ProfileState === 'Active' ? (
                      targetProfileId && <DisableProfile targetProfileId={targetProfileId} onSuccess={() => handleProfileUpdate({ ProfileState: 'Inactive' })} />
                    ) : (
                      targetProfileId && <ActivateProfile targetProfileId={targetProfileId} onSuccess={() => handleProfileUpdate({ ProfileState: 'Active' })} />
                    )}
                    {!profile.Approved && targetProfileId &&
                      ( <ApproveProfile targetProfileId={targetProfileId} uniqueId={uniqueId}onSuccess={() => handleProfileUpdate({ Approved: true })}/> )
                    }
                    {targetProfileId && ( <UnlockUser targetProfileId={targetProfileId} isDisabled={profile.ProfileState !== 'Locked'} onSuccess={() => handleProfileUpdate({ ProfileState: 'Active' })}/>)}
                    {targetProfileId && (<VerifyUserEmail targetProfileId={targetProfileId} emailVerified={profile?.EmailVerified} onSuccess={() => fetchProfileDetails()} />)} 
                    {targetProfileId && (<DeleteProfile targetProfileId={targetProfileId} />)} 
                  </>
                )}
              </div>
            </div>
            {/* Right side */}
            <div className="col-12 col-lg-4">
              {profile?.UserId &&
                (isManageProfile && isAdminWeb) &&
                (profile.ProfileType === 'Spoke' || profile.Category === 'Spoke') && (
                  <SpokeAllowedTransactions spokeUserId={profile.UserId} />
                )}

              <div className="card shadow-sm mb-4">
                <div className="card-header bg-light py-3">
                  <h6 className="mb-0 fw-bold">
                    {t('ProfileRequiredActions')}
                    <i className="bi bi-info-circle ms-2 text-muted" data-bs-toggle="tooltip" title={t('ProfileActionsInfo')} style={{ cursor: "pointer" }}></i>
                  </h6>
                </div>
                <div className="card-body d-grid gap-4">
                  {(isManageProfile && isAdminWeb) && (
                    <>
                      {targetProfileId && <AssignOtpAppSetup targetProfileId={targetProfileId} />}
                      {targetProfileId &&  <UnassignOtpAppSetup targetProfileId={targetProfileId} />}
                      {targetProfileId &&  <ResetPassword targetProfileId={targetProfileId} />}
                      {targetProfileId && <SendResetPassword targetProfileId={targetProfileId} />}
                    </>
                  )}
                  {targetProfileId && (isHelpDeskOperator && isAdminWeb) && <ResendActivationLink targetProfileId={targetProfileId} />}
                </div>
              </div>

              {/* <div className="card shadow-sm">
                <div className="card-header bg-light py-3">
                  <h6 className="mb-0 fw-bold">
                    {t('GetActionLinks')}
                    <i className="bi bi-info-circle ms-2 text-muted" data-bs-toggle="tooltip" title={t('ActionLinks')} style={{ cursor: "pointer" }}></i>
                  </h6>
                </div>
                <div className="card-body">
                  {targetProfileId && <GetActionLink targetProfileId={targetProfileId} />}
                </div>
              </div> */}
            </div>
          </div>
        )}
     </>
    )}
  </div>
  );
}
